import { createSlice } from "@reduxjs/toolkit";
import { addDays, addMonths, addYears, format, isBefore, min, differenceInDays } from 'date-fns';
import qs from 'query-string';
import { fetchPriceGraphRequest } from '../../api';
import { parseDate } from '../../utilities/helpers';

const hashParams = qs.parse(window.location.hash);

let hashNights = hashParams.nights;
if (!hashNights && hashParams.type === 'specific' && hashParams.startDate && hashParams.endDate) {
  const start = new Date(hashParams.startDate.split('-').reverse().join('-'));
  const end = new Date(hashParams.endDate.split('-').reverse().join('-'));
  hashNights = differenceInDays(end, start);
}

const initialState = {
  hotel: {},
  destination: {},
  firstVisibleDate: null,
  pending: false,
  error: false,
  nights: parseInt(hashNights, 10) || 4,
};

export const priceGraphSlice = createSlice({
  name: "dealsList",
  initialState,
  reducers: {
    getPriceGraph: (state, action) => ({
      ...state,
      pending: true
    }),
    getPriceGraphSuccess: (state, action) => {
      return {
        ...state,
        ...action.payload.data,
        pending: false
      }
    },
    setFirstVisibleDate: (state, action) => ({
      ...state,
      firstVisibleDate: action.payload,
    }),
    getPriceGraphRangeSuccess: (state, action) => {
      state.destination[action.payload.destination].dates = action.payload.data;
      state.pending = false;
    },
    setPriceGraphNights: (state, action) => {
      state.nights = action.payload;
    }
  }
});

export const {
  getPriceGraphSuccess,
  getPriceGraph,
  setFirstVisibleDate,
  getPriceGraphRangeSuccess,
  setPriceGraphNights,
} = priceGraphSlice.actions;

const getInitialDatesRange = (when) => {
  const today = new Date();
  let rangeStart = today;
  let rangeEnd = addMonths(today, 2);

  if (when.type === 'month' && when.months.length > 0) {
    const currentMonth = today.getMonth();

    let months = when.months.map(month => {
      let parsedMonth = parseDate(month, 'M');
      if (parsedMonth.getMonth() < currentMonth) {
        parsedMonth = addYears(parsedMonth, 1);
      }

      return parsedMonth;
    })
    rangeStart = addMonths(min(months), -1);
    rangeEnd = addMonths(rangeStart, 2);
  }

  if (when.type === 'specific' || when.type === 'range') {
    const startDate = when.startDate ? parseDate(when.startDate) : new Date();
    rangeStart = addMonths(startDate, -1);
    rangeEnd = addMonths(rangeStart, 2);
  }

  if (isBefore(rangeStart, new Date())) {
    rangeStart = new Date()
    rangeEnd = addMonths(rangeStart, 2);
  }

  return {
    start: format(rangeStart, 'dd/MM/yyyy'),
    end: format(rangeEnd, 'dd/MM/yyyy'),
  };
}

const getPriceGraphRequestData = (state, destinationId) => {
  const who = state.mainFilter.searchEngine.who;
  const fromwhere = state.mainFilter.searchEngine.fromwhere;
  let selectedDeparture = fromwhere.filter(item => item.active);
  if (!selectedDeparture.length) {
    selectedDeparture = [fromwhere[0]];
  }
  const what = state.mainFilter.searchEngine.what.filter(({ active }) => active).map(({ id }) => id);
  const rating = state.secondaryFilter.data.stars.filter(({ active }) => active).map(({ value }) => value);
  const stops = state.secondaryFilter.data.stops.filter(({ active }) => active).map(({ value }) => value);
  const board = state.secondaryFilter.data.board.filter(({ active }) => active).map(({ value }) => value.toUpperCase());
  const amenities = state.secondaryFilter.data.facilities.filter(({ active }) => active).map(({ value }) => value);
  const flightDuration = state.secondaryFilter.data.duration.activeTime;
  const luggage = {
    canAddTrolley: state.secondaryFilter.data?.luggage?.filter(({ value }) => (value === 'canAddTrolley'))[0]?.active || false,
    canAddCib: state.secondaryFilter.data?.luggage?.filter(({ value }) => (value === 'canAddCib'))[0]?.active || false
  };
  const filters = {
    rating,
    stops,
    refundable: state.secondaryFilter.data.freeCancellation,
    board,
    amenities,
    flight_duration: flightDuration,
    luggage,
    flex: state.secondaryFilter.data?.flex?.active || false,
  };

  return {
    fromwhere: selectedDeparture.map(d => d.id),
    destination: destinationId,
    who: {
      adult: who.adults,
      child: who.children,
      room: who.rooms,
      childAges: who.childAges
    },
    what,
    filters,
  }
}

const getDatesRangeStartEnd = (state, destinationId, rangeDirection) => {
  const destinationDates = state.priceGraph.destination[destinationId].dates;
  let rangeStart, rangeEnd;

  if (rangeDirection > 0) {
    rangeStart = format(addDays(parseDate(destinationDates[destinationDates.length - 1].start), 1), 'dd/MM/yyyy');
    rangeEnd = format(addMonths(parseDate(rangeStart), 1), 'dd/MM/yyyy');
  } else {
    const arrayStart = parseDate(destinationDates[0].start);
    rangeEnd = format(addDays(arrayStart, -1), 'dd/MM/yyyy');
    rangeStart = format(addMonths(parseDate(rangeEnd), -1), 'dd/MM/yyyy');
  }

  return { rangeStart, rangeEnd };
};

export const fetchPriceGraph = (destinationId, nights, rangeDirection) => async (dispatch, getState) => {
  const { search: appSearch, mainFilter: { searchEngine: { when: dates } }, priceGraph } = getState();
  const duration = nights || dates.minNights;

  if (rangeDirection) {
    const { rangeStart, rangeEnd } = getDatesRangeStartEnd(getState(), destinationId, rangeDirection);
    const payload = {
      ...getPriceGraphRequestData(getState(), destinationId),
      when: {
        flexible: {
          duration,
          start: rangeStart,
          end: rangeEnd,
        },
      },
    };

    try {
      dispatch(getPriceGraph());
      const response = await fetchPriceGraphRequest(payload);
      const destinationData = response.destination[destinationId].dates;
      const data = rangeDirection > 0
        ? [...priceGraph.destination[destinationId].dates, ...destinationData]
        : [...destinationData, ...priceGraph.destination[destinationId].dates];

      dispatch(getPriceGraphRangeSuccess({ data, destination: destinationId }));
    } catch (error) {
      console.error(error);
    }
  } else {
    const payload = {
      ...getPriceGraphRequestData(getState(), destinationId),
      when: {
        flexible: {
          duration,
          ...getInitialDatesRange(appSearch.when),
        },
      },
    };

    try {
      dispatch(getPriceGraph());
      const response = await fetchPriceGraphRequest(payload);
      dispatch(getPriceGraphSuccess({ data: response, destination: destinationId }));
    } catch (error) {
      console.error(error);
    }
  }
};

// TODO
const fetchInitialPriceGraph = () => {};

// TODO
const fetchPriceGraphRange = () => {};

export const selectDestinationGraph = (state) => state.priceGraph;

export default priceGraphSlice.reducer;
